/**
 * Minimal string-similarity helpers for did-you-mean matching (e.g. reconciling
 * a typed calibre tier name against a family's canonical vocabulary). No external
 * dependencies.
 */

/** Normalize for insensitive comparison: strip diacritics + lowercase + trim. */
export function normalizeForCompare(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Standard Levenshtein edit distance (insertions / deletions / substitutions)
 * using a single rolling row. Callers should normalize inputs first if the
 * comparison should be case/accent-insensitive.
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // prev[j] = distance between a[..] and the first j chars of b.
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // deletion
        curr[j - 1] + 1, // insertion
        prev[j - 1] + cost, // substitution
      );
    }
    prev = curr;
  }
  return prev[b.length];
}

/**
 * Returns the closest candidate to `input` within an edit-distance threshold, or
 * null when nothing is close enough. Comparison is case- and accent-insensitive
 * (both sides normalized). The threshold is length-tiered so short strings need a
 * tighter bound (≤ 1) than longer ones (≤ 2) to avoid unrelated short words
 * false-matching — capped by `maxDistance`. The returned string is the original
 * candidate (canonical casing/accents preserved).
 */
export function findClosestMatch(
  input: string,
  candidates: string[],
  maxDistance = 2,
): string | null {
  const normInput = normalizeForCompare(input);
  if (!normInput) return null;
  const threshold = Math.min(maxDistance, normInput.length >= 5 ? 2 : 1);

  let best: string | null = null;
  let bestDist = Infinity;
  for (const candidate of candidates) {
    const dist = levenshteinDistance(normInput, normalizeForCompare(candidate));
    if (dist <= threshold && dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}
